import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { CreateRecordProperties, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import GoalLog from 'webapp/models-custom/goal-log';
import type GoalLogRecord from 'webapp/models/goal-log';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export default class ModelManagerGoalLogsService extends ModelManagerForStoreService<
	GoalLogRecord,
	GoalLog
> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	private _goalLogModels: GoalLog[] = [];

	@cached
	get goalLogModels(): GoalLog[] {
		this.updateModelCache(this._goalLogModels, this.savedRecords);
		return this._goalLogModels;
	}

	getLogsForGoal(goalId: string): GoalLog[] {
		return sortBy(
			this.goalLogModels.filter((log) => log.goalId === +goalId),
			'created',
		).reverse();
	}

	requestGoalLogs(goalId: string): Promise<void> {
		return this.requestService
			.api<RecordData.Payload>('GET', `/goals/${goalId}/logs`, null, { timeout: 60 * 1000 })
			.then((payload) => {
				this.storeService.pushPayload(payload);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createOrUpdateGoalLog(model: GoalLog): Promise<void> {
		const infoText = model.isNew ? 'Creat' : 'Updat';
		const layer = this.infolayerService.loading(`${infoText}ing goal log...`);

		const trackingEvent = model.isNew
			? TrackingEvent.GOAL_PROGRESS_UPDATED
			: TrackingEvent.GOAL_PROGRESS_EDITED;

		this.analyticsService.event(trackingEvent, {
			title: model.goal.title,
			status: this.mmGoalsService.getStatusText(model.status),
			origin: 'goals',
		});

		return model
			.save()
			.then(
				async () => {
					const blockId = model.goal.blockId;
					await Promise.all([
						this.mmGoalsService.requestSingleGoal(String(model.goalId)),
						blockId
							? this.mmBlocksService.requestSingleBlock(String(blockId), true)
							: Promise.resolve(),
					]);
					layer.ok(`Goal log ${infoText.toLowerCase()}ed!`);
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	deleteGoalLog(model: GoalLog): Promise<void> {
		const layer = this.infolayerService.loading('Deleting goal log...');
		return model
			.delete()
			.then(
				() => {
					layer.ok('Goal log deleted!');
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	modelName = ModelName.GOAL_LOG;

	createModel(model: GoalLogRecord): GoalLog {
		return new GoalLog({ model });
	}

	override createNewModel(properties: CreateRecordProperties<GoalLogRecord> = {}): GoalLog {
		if (typeof properties.createdBy === 'undefined') {
			properties.createdBy = this.currentUserService.numericId!;
		}
		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/goal-logs': ModelManagerGoalLogsService;
	}
}
